import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Percent,
  Receipt,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GlassCard,
  GlassCardHeader,
  GlassStat,
  GlassChip,
  GlassBadge,
  GlassAvatar,
  GlassSearch,
  GlassSelect,
  GlassEmpty,
  SlidingPills,
  glassChartTheme,
} from '../glass/GlassUi';
import { PinnableCard } from './reportCards';
import {
  buildClientPnlRows,
  buildMonthExpenseLedger,
  buildMonthlyPnl,
  buildRunRateSummary,
  formatCurrency,
  groupExpensesByPartner,
} from '../../utils/financeReportMetrics';
import { describeClientLifecycle } from '../../utils/accountLifecycle';
import { parseExpenseDate } from '../../services/accountExpensesService';

const COLLECTED_COLOR = GLASS_TONE.mint;
const EXPENSE_COLOR = GLASS_TONE.red;
const PROFIT_COLOR = GLASS_TONE.blue;

const SCOPE_OPTIONS = [
  { value: 'active', label: 'Active clients' },
  { value: 'ended', label: 'Ended clients' },
  { value: 'all', label: 'All clients' },
];

// Lifecycle label → glass badge tone (describeClientLifecycle supplies the label)
const LIFECYCLE_TONE = { Active: 'mint', Canceling: 'amber', Canceled: 'coral', Lost: 'red', Archived: 'neutral' };

const MUTED = 'text-gray-500 dark:text-white/[0.45]';

function formatSigned(value) {
  return value < 0 ? `-${formatCurrency(Math.abs(value))}` : formatCurrency(value);
}

function formatMargin(margin) {
  return margin === null || margin === undefined ? '—' : `${Math.round(margin * 100)}%`;
}

function fmtAxisCurrency(v) {
  const n = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (n >= 1000) return `${sign}$${Math.round(n / 1000)}k`;
  return `${sign}$${n}`;
}

function profitClass(value) {
  return value < 0 ? GLASS_TEXT.red : GLASS_TEXT.mint;
}

/** Tinted pill: red when losing money, amber under 30%, mint otherwise. */
function MarginPill({ margin }) {
  if (margin === null || margin === undefined) {
    return <span className="text-gray-400 dark:text-white/30">—</span>;
  }
  const tone = margin < 0 ? 'red' : margin < 0.3 ? 'amber' : 'mint';
  return <GlassBadge tone={tone}>{formatMargin(margin)}</GlassBadge>;
}

function StatusPill({ status }) {
  const tone = status === 'paid' ? 'mint' : status === 'failed' ? 'red' : 'amber';
  return (
    <GlassBadge tone={tone}>
      <span className="capitalize">{status}</span>
    </GlassBadge>
  );
}

/** Glass tooltip with signed-currency values (GlassTooltip shows raw numbers). */
function MoneyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-2 text-xs shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#14110f]/90">
      {label !== undefined && label !== '' && <div className="mb-1 text-gray-500 dark:text-white/[0.45]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color || p.stroke || p.fill }} />
          <span className="text-gray-700 dark:text-white/75">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold text-gray-900 tabular-nums dark:text-white">{formatSigned(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
}

function expenseSourceLabel(source, now) {
  const month = (offset) =>
    new Date(now.getFullYear(), now.getMonth() + offset, 1).toLocaleDateString('en-US', { month: 'short' });
  if (source === 'manual') return 'Manual';
  if (source === 'this-month') return `${month(0)} logged`;
  if (source === 'last-month') return `${month(-1)} logged`;
  return 'None logged';
}

const SORTS = {
  profit: { label: 'Monthly profit', get: (r) => r.monthlyProfit },
  margin: { label: 'Margin', get: (r) => (r.monthlyMargin ?? -Infinity) },
  mrr: { label: 'MRR', get: (r) => r.mrr },
  expenses: { label: 'Monthly expenses', get: (r) => r.monthlyExpenses },
  lifetime: { label: 'Lifetime profit', get: (r) => r.lifetimeProfit },
};

/**
 * Finance Reports ▸ Profit & Loss: MRR vs monthly expenses vs profit from the client accounts
 * (program/billing amounts, Billing-tab expenses, paid invoices) plus internal expenses.
 */
const FinanceProfitLossTab = ({ clientAccounts, paymentsByAccountId, expensesByAccountId, internalExpenses, darkMode }) => {
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);

  const clientRows = useMemo(
    () => buildClientPnlRows({ clients: clientAccounts, paymentsByAccountId, expensesByAccountId, now }),
    [clientAccounts, paymentsByAccountId, expensesByAccountId, now],
  );
  const runRate = useMemo(() => buildRunRateSummary(clientRows, internalExpenses, now), [clientRows, internalExpenses, now]);
  const monthly = useMemo(
    () =>
      buildMonthlyPnl({
        clients: clientAccounts,
        paymentsByAccountId,
        expensesByAccountId,
        internalExpenses,
        now,
      }),
    [clientAccounts, paymentsByAccountId, expensesByAccountId, internalExpenses, now],
  );
  const monthlyTotals = useMemo(() => {
    const t = monthly.reduce(
      (acc, m) => ({ collected: acc.collected + m.collected, expenses: acc.expenses + m.expenses }),
      { collected: 0, expenses: 0 },
    );
    const profit = t.collected - t.expenses;
    return { ...t, profit, margin: t.collected > 0 ? profit / t.collected : null };
  }, [monthly]);

  // Expense drill-down month (defaults to the current month, the last row)
  const [selectedIdx, setSelectedIdx] = useState(monthly.length - 1);
  const selectedMonth = monthly[selectedIdx] || monthly[monthly.length - 1];
  const ledger = useMemo(
    () =>
      selectedMonth
        ? buildMonthExpenseLedger({
            clients: clientAccounts,
            expensesByAccountId,
            internalExpenses,
            monthStart: selectedMonth.monthStart,
          })
        : [],
    [clientAccounts, expensesByAccountId, internalExpenses, selectedMonth],
  );
  const byPartner = useMemo(() => groupExpensesByPartner(ledger), [ledger]);
  const maxPartnerAmount = Math.max(1, ...byPartner.map((p) => p.amount));

  // Client profitability table
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('active');
  const [sortKey, setSortKey] = useState('profit');
  const [sortAsc, setSortAsc] = useState(false);
  const visibleClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorter = SORTS[sortKey] || SORTS.profit;
    return clientRows
      .filter((r) => (scope === 'all' ? true : scope === 'ended' ? !r.active : r.active))
      .filter((r) => !q || r.clientName.toLowerCase().includes(q))
      .sort((a, b) => (sortAsc ? sorter.get(a) - sorter.get(b) : sorter.get(b) - sorter.get(a)));
  }, [clientRows, search, scope, sortKey, sortAsc]);
  const clientTotals = useMemo(
    () =>
      visibleClients.reduce(
        (acc, r) => ({
          mrr: acc.mrr + r.mrr,
          monthlyExpenses: acc.monthlyExpenses + r.monthlyExpenses,
          monthlyProfit: acc.monthlyProfit + r.monthlyProfit,
          lifetimeCollected: acc.lifetimeCollected + r.lifetimeCollected,
          lifetimeExpenses: acc.lifetimeExpenses + r.lifetimeExpenses,
          lifetimeProfit: acc.lifetimeProfit + r.lifetimeProfit,
        }),
        { mrr: 0, monthlyExpenses: 0, monthlyProfit: 0, lifetimeCollected: 0, lifetimeExpenses: 0, lifetimeProfit: 0 },
      ),
    [visibleClients],
  );

  useEffect(() => {
    setSelectedIdx(monthly.length - 1);
  }, [monthly.length]);

  const chart = glassChartTheme(darkMode);
  const axisTick = { fontSize: 11, fill: chart.tick };

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };
  const sortHeader = (key, label, extra = '') => (
    <th className={`${GLASS_TH} whitespace-nowrap ${extra}`}>
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900 dark:hover:text-white ${
          sortKey === key ? 'text-gray-900 dark:text-white' : ''
        }`}
      >
        {label}
        {sortKey === key && <span aria-hidden>{sortAsc ? '▲' : '▼'}</span>}
      </button>
    </th>
  );

  const internalSourceText =
    runRate.internalSource === 'none' ? 'none logged' : expenseSourceLabel(runRate.internalSource, now);

  const monthNavBtn = 'grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/40 text-gray-700 transition hover:border-[#F80707]/50 hover:text-[#F80707] disabled:pointer-events-none disabled:opacity-30 dark:border-white/[0.15] dark:bg-white/[0.04] dark:text-white/70 dark:hover:border-[#F80707]/60 dark:hover:text-[#FF4D40]';
  const cell = 'py-3 pr-4 whitespace-nowrap';

  return (
    <div className="space-y-4">
      {/* Run-rate */}
      <PinnableCard id="run-rate" title="Monthly run-rate" size="wide">
      <GlassCard className="p-5" delay={0}>
        <GlassCardHeader title="Monthly run-rate" />
        <p className={`mt-1 text-xs ${MUTED}`}>
          Active clients' monthly amounts minus what they cost us each month, plus internal costs.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className={`${GLASS_INSET} p-4`}>
            <GlassStat icon={DollarSign} label="MRR" value={formatCurrency(runRate.mrr)} valueClassName={GLASS_TEXT.mint} />
          </div>
          <div className={`${GLASS_INSET} p-4`}>
            <GlassStat icon={Receipt} label="Monthly expenses" value={formatCurrency(runRate.expenses)} valueClassName={GLASS_TEXT.red} />
          </div>
          <div className={`${GLASS_INSET} p-4`}>
            <GlassStat
              icon={runRate.profit < 0 ? TrendingDown : TrendingUp}
              label="Monthly profit"
              value={formatSigned(runRate.profit)}
              valueClassName={profitClass(runRate.profit)}
            />
          </div>
          <div className={`${GLASS_INSET} p-4`}>
            <GlassStat icon={Percent} label="Profit margin" value={formatMargin(runRate.margin)} />
          </div>
        </div>
        <p className={`mt-4 text-xs ${MUTED}`}>
          {runRate.activeClients} active clients · client costs {formatCurrency(runRate.clientExpenses)} · internal costs{' '}
          {formatCurrency(runRate.internalExpenses)} ({internalSourceText}). A client's manual Monthly Expenses (Billing
          tab) wins; otherwise the larger of this month's and last month's logged expenses is used.
        </p>
      </GlassCard>
      </PinnableCard>

      {/* Monthly actuals */}
      <PinnableCard id="pnl-12-months" title="Profit & loss — last 12 months" size="wide">
      <GlassCard className="p-5" delay={80}>
        <GlassCardHeader title={<>Profit &amp; loss — last 12 months</>} />
        <p className={`mt-1 text-xs ${MUTED}`}>
          Cash collected from paid client invoices vs. paid expenses, by month. Click a month to see its expenses.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${MUTED}`}>Collected</div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${GLASS_TEXT.mint}`}>{formatCurrency(monthlyTotals.collected)}</div>
          </div>
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${MUTED}`}>Expenses</div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${GLASS_TEXT.red}`}>{formatCurrency(monthlyTotals.expenses)}</div>
          </div>
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${MUTED}`}>Profit</div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${profitClass(monthlyTotals.profit)}`}>{formatSigned(monthlyTotals.profit)}</div>
          </div>
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${MUTED}`}>Margin</div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${GLASS_TEXT.strong}`}>{formatMargin(monthlyTotals.margin)}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <GlassChip color={COLLECTED_COLOR} label="Collected" />
          <GlassChip color={EXPENSE_COLOR} label="Expenses" />
          <GlassChip color={PROFIT_COLOR} label="Profit" />
        </div>

        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthly}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              barGap={4}
              barCategoryGap="22%"
              onClick={(e) => {
                if (e && typeof e.activeTooltipIndex === 'number') setSelectedIdx(e.activeTooltipIndex);
              }}
            >
              <CartesianGrid strokeDasharray="4 6" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmtAxisCurrency} tick={axisTick} axisLine={false} tickLine={false} width={56} />
              <Tooltip content={<MoneyTooltip />} cursor={{ fill: chart.cursor }} />
              <ReferenceLine y={0} stroke={chart.grid} />
              <Bar dataKey="collected" name="Collected" fill={COLLECTED_COLOR} radius={[8, 8, 8, 8]} maxBarSize={22} background={{ fill: chart.track, radius: 8 }} cursor="pointer" animationDuration={700} />
              <Bar dataKey="expenses" name="Expenses" fill={EXPENSE_COLOR} radius={[8, 8, 8, 8]} maxBarSize={22} background={{ fill: chart.track, radius: 8 }} cursor="pointer" animationDuration={700} />
              <Line type="monotone" dataKey="profit" name="Profit" stroke={PROFIT_COLOR} strokeWidth={2.5} dot={{ r: 3, fill: PROFIT_COLOR, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr>
                {['Month', 'Collected', 'Client costs', 'Internal costs', 'Total expenses', 'Profit', 'Margin'].map((h) => (
                  <th key={h} className={`${GLASS_TH} whitespace-nowrap`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-white/75">
              {[...monthly].reverse().map((m) => {
                const idx = monthly.indexOf(m);
                const selected = idx === selectedIdx;
                return (
                  <tr
                    key={m.key}
                    onClick={() => setSelectedIdx(idx)}
                    className={`${GLASS_ROW} cursor-pointer ${
                      selected ? 'bg-[#F80707]/[0.06] hover:bg-[#F80707]/[0.08] dark:bg-[#F80707]/10 dark:hover:bg-[#F80707]/[0.12]' : ''
                    }`}
                  >
                    <td className={`${cell} font-medium text-gray-900 dark:text-white`}>
                      {m.longLabel}
                      {m.isCurrent && <span className={`ml-2 text-xs font-normal ${MUTED}`}>to date</span>}
                    </td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(m.collected)}</td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(m.clientExpenses)}</td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(m.internalExpenses)}</td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(m.expenses)}</td>
                    <td className={`${cell} font-semibold tabular-nums ${profitClass(m.profit)}`}>{formatSigned(m.profit)}</td>
                    <td className="whitespace-nowrap py-3">
                      <MarginPill margin={m.margin} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>
      </PinnableCard>

      {/* Expenses for the selected month */}
      {selectedMonth && (
        <PinnableCard id="month-expenses" title="Expenses by month" size="wide">
        <GlassCard className="p-5" delay={140}>
          <GlassCardHeader
            title={`Expenses · ${selectedMonth.longLabel}`}
            right={(
              <>
                <button
                  type="button"
                  aria-label="Previous month"
                  disabled={selectedIdx <= 0}
                  onClick={() => setSelectedIdx((i) => Math.max(0, i - 1))}
                  className={monthNavBtn}
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  aria-label="Next month"
                  disabled={selectedIdx >= monthly.length - 1}
                  onClick={() => setSelectedIdx((i) => Math.min(monthly.length - 1, i + 1))}
                  className={monthNavBtn}
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </>
            )}
          />
          <p className={`mb-4 mt-1 text-xs ${MUTED}`}>
            <span className={GLASS_TEXT.red}>{formatCurrency(selectedMonth.expenses)}</span> paid out ·{' '}
            <span className={profitClass(selectedMonth.profit)}>{formatSigned(selectedMonth.profit)}</span> profit on{' '}
            <span className={GLASS_TEXT.mint}>{formatCurrency(selectedMonth.collected)}</span> collected
          </p>

          {ledger.length === 0 ? (
            <GlassEmpty className="py-8">
              No expenses logged in {selectedMonth.longLabel}. Expenses come from each client's Billing tab and internal
              expenses on Partner pages.
            </GlassEmpty>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className={`${GLASS_INSET} p-5`}>
                <h3 className="mb-4 text-sm font-medium text-gray-800 dark:text-white/[0.85]">By partner</h3>
                {byPartner.length === 0 ? (
                  <p className={`text-xs ${MUTED}`}>No paid expenses yet this month.</p>
                ) : (
                  <ul className="space-y-4">
                    {byPartner.map((p) => (
                      <li key={p.key}>
                        <div className="flex items-baseline justify-between gap-2 text-sm">
                          <span className="truncate font-medium text-gray-900 dark:text-white">{p.name}</span>
                          <span className={`shrink-0 font-semibold tabular-nums ${GLASS_TEXT.red}`}>{formatCurrency(p.amount)}</span>
                        </div>
                        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.07]">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(p.amount / maxPartnerAmount) * 100}%`, backgroundColor: EXPENSE_COLOR }}
                          />
                        </div>
                        <div className={`mt-1 truncate text-xs ${MUTED}`}>
                          {p.count} payment{p.count === 1 ? '' : 's'} · {p.clients.join(', ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="overflow-x-auto xl:col-span-2">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr>
                      {['Date', 'Partner', 'Client', 'Description', 'Status', 'Amount'].map((h) => (
                        <th key={h} className={`${GLASS_TH} whitespace-nowrap`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-white/75">
                    {ledger.map((e) => (
                      <tr
                        key={`${e.accountId || 'internal'}-${e.id}`}
                        onClick={() => e.accountId && navigate(`/crm/client/${e.accountId}`)}
                        className={`${GLASS_ROW} ${e.accountId ? 'cursor-pointer' : ''}`}
                      >
                        <td className={cell}>
                          {parseExpenseDate(e)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '—'}
                        </td>
                        <td className={`${cell} font-medium text-gray-900 dark:text-white`}>
                          {e.subcontractorName || 'Unassigned'}
                        </td>
                        <td className={cell}>{e.clientName}</td>
                        <td className={`max-w-[260px] truncate py-3 pr-4 ${MUTED}`} title={e.description}>
                          {e.description || '—'}
                        </td>
                        <td className={cell}>
                          <StatusPill status={e.status} />
                        </td>
                        <td className={`whitespace-nowrap py-3 font-semibold tabular-nums ${GLASS_TEXT.red}`}>
                          {formatCurrency(e.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className={`mt-3 text-xs ${MUTED}`}>
                  Only paid expenses count toward totals; processing and failed ones are listed for reference.
                </p>
              </div>
            </div>
          )}
        </GlassCard>
        </PinnableCard>
      )}

      {/* Client profitability */}
      <PinnableCard id="client-profitability" title="Client profitability" size="wide">
      <GlassCard className="p-5" delay={200}>
        <GlassCardHeader title="Client profitability" />
        <p className={`mt-1 text-xs ${MUTED}`}>
          Monthly numbers match each client's Billing tab. Lifetime = every paid invoice minus every paid expense.
        </p>

        <div className="mb-4 mt-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <GlassSearch
            className="flex-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients…"
          />
          <SlidingPills size="sm" options={SCOPE_OPTIONS} value={scope} onChange={setScope} ariaLabel="Client scope" />
          <GlassSelect value={sortKey} onChange={(e) => { setSortKey(e.target.value); setSortAsc(false); }} aria-label="Sort by">
            {Object.entries(SORTS).map(([key, s]) => (
              <option key={key} value={key}>
                Sort: {s.label}
              </option>
            ))}
          </GlassSelect>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Client</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Status</th>
                {sortHeader('mrr', 'MRR')}
                {sortHeader('expenses', 'Monthly expenses')}
                {sortHeader('profit', 'Monthly profit')}
                {sortHeader('margin', 'Margin')}
                <th className={`${GLASS_TH} whitespace-nowrap`}>Lifetime collected</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Lifetime expenses</th>
                {sortHeader('lifetime', 'Lifetime profit')}
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-white/75">
              {visibleClients.map((r) => {
                const lc = describeClientLifecycle(r.lifecycleState);
                return (
                  <tr
                    key={r.accountId}
                    onClick={() => navigate(`/crm/client/${r.accountId}`)}
                    className={`${GLASS_ROW} cursor-pointer`}
                  >
                    <td className={cell}>
                      <div className="flex items-center gap-3">
                        <GlassAvatar name={r.clientName} />
                        <span className="font-medium text-gray-900 dark:text-white">{r.clientName}</span>
                      </div>
                    </td>
                    <td className={cell}>
                      <GlassBadge tone={LIFECYCLE_TONE[lc?.label] || 'neutral'}>
                        {lc?.label || r.lifecycleState}
                      </GlassBadge>
                    </td>
                    <td className={`${cell} font-semibold tabular-nums ${GLASS_TEXT.mint}`}>{formatCurrency(r.mrr)}</td>
                    <td className={`${cell} tabular-nums`}>
                      <span className={GLASS_TEXT.red}>{formatCurrency(r.monthlyExpenses)}</span>
                      {r.active && (
                        <span className={`block text-[11px] ${MUTED}`}>
                          {expenseSourceLabel(r.expenseSource, now)}
                        </span>
                      )}
                    </td>
                    <td className={`${cell} font-semibold tabular-nums ${profitClass(r.monthlyProfit)}`}>
                      {formatSigned(r.monthlyProfit)}
                    </td>
                    <td className={cell}>
                      <MarginPill margin={r.monthlyMargin} />
                    </td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(r.lifetimeCollected)}</td>
                    <td className={`${cell} tabular-nums`}>{formatCurrency(r.lifetimeExpenses)}</td>
                    <td className={`whitespace-nowrap py-3 font-semibold tabular-nums ${profitClass(r.lifetimeProfit)}`}>
                      {formatSigned(r.lifetimeProfit)}
                    </td>
                  </tr>
                );
              })}
              {visibleClients.length === 0 && (
                <tr>
                  <td colSpan={9} className={`py-8 text-center text-sm ${MUTED}`}>
                    {search ? 'No clients match your search.' : 'No clients in this view.'}
                  </td>
                </tr>
              )}
            </tbody>
            {visibleClients.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-black/10 font-semibold dark:border-white/10">
                  <td className="py-3 pr-4 text-gray-900 dark:text-white" colSpan={2}>
                    Total · {visibleClients.length} client{visibleClients.length === 1 ? '' : 's'}
                  </td>
                  <td className={`${cell} tabular-nums ${GLASS_TEXT.mint}`}>{formatCurrency(clientTotals.mrr)}</td>
                  <td className={`${cell} tabular-nums ${GLASS_TEXT.red}`}>{formatCurrency(clientTotals.monthlyExpenses)}</td>
                  <td className={`${cell} tabular-nums ${profitClass(clientTotals.monthlyProfit)}`}>
                    {formatSigned(clientTotals.monthlyProfit)}
                  </td>
                  <td className={cell}>
                    <MarginPill margin={clientTotals.mrr > 0 ? clientTotals.monthlyProfit / clientTotals.mrr : null} />
                  </td>
                  <td className={`${cell} tabular-nums text-gray-900 dark:text-white`}>{formatCurrency(clientTotals.lifetimeCollected)}</td>
                  <td className={`${cell} tabular-nums text-gray-900 dark:text-white`}>{formatCurrency(clientTotals.lifetimeExpenses)}</td>
                  <td className={`whitespace-nowrap py-3 tabular-nums ${profitClass(clientTotals.lifetimeProfit)}`}>
                    {formatSigned(clientTotals.lifetimeProfit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </GlassCard>
      </PinnableCard>
    </div>
  );
};

export default FinanceProfitLossTab;
